<template>
  <div class="advanced-row">
    <div class="advanced-row__field"
      :class="{ 'advanced-row_error': isFieldError }">
      <base-smart-input class="advanced-row__select"
        :options="clonedOptions"
        :selectStyle="{ height: '60px!important' }"
        borderLess
        easyValueModel
        v-model="option.selectedField"
        @change="onFieldChanged"
        placeholder="Field"
        :isNotValid="isFieldError" />
    </div>
    <div class="advanced-row__type"
      :class="{ 'advanced-row_error': isOperatorError }">
      <base-smart-input class="advanced-row__select"
        :disabled="!option.selectedField"
        :options="operators"
        :selectStyle="{ height: '60px!important' }"
        borderLess
        easyValueModel
        v-model="option.selectedOperator"
        @change="onOptionChanged"
        placeholder="Operator"
        :isNotValid="isOperatorError" />
    </div>
    <div class="advanced-row__filter"
      private-content="true"
      :class="{ 'advanced-row_error': isFilterError }">
      <input class="advanced-row__input-placeholder"
        v-if="!filterType"
        type="text"
        disabled
        placeholder="Field value" />
      <template v-else="!!filterType">
        <base-smart-input class="advanced-row__select"
          v-if=isSmartInputMode
          ref="smartInput"
          @change="onOptionChanged"
          :allowFreeText="option.selectedOperator === 'contains'"
          :options="filters"
          :selectStyle="{ height: '60px!important' }"
          :borderLess="true"
          v-model="option.selectedFilter"
          placeholder="Select list"
          :isNotValid="isFilterError" />
        <base-smart-input-autocomplete class="advanced-row__select"
          v-else-if="isAutocompleteMode"
          :options="autocompleteFilters"
          :selectStyle="{ height: '60px!important' }"
          :borderLess="true"
          v-model="option.selectedFilter"
          @state="onStateChange"
          @input-text="onAutocompleteInputTextChanged($event)"
          placeholder="Enter text"
          :isNotValid="isFilterError">
          <base-horizontal-loader class="advanced-row__loader"
            v-if="isSmartInputFocused && isAutocompleteMode && isAutocompleteDataUpdating != 0" />
        </base-smart-input-autocomplete>
        <input class="advanced-row__input"
          :class="{ 'advanced-row__input_error': isFilterError }"
          v-else-if="isInputStringMode"
          type="text"
          v-model="option.selectedFilter.text"
          placeholder="Type text" />
        <!-- for now we don't use number input -->
        <input class="advanced-row__input"
          :class="{ 'advanced-row__input_error': isFilterError }"
          v-else-if=isNumberInputMode
          v-model="option.selectedFilter.text"
          v-digitsOnly
          placeholder="Type number" />
      </template>
    </div>
    <div class="advanced-row__button"
        @click.stop="onDeleteOption">
      <base-material-design-icon class="advancer-row__close-icon" 
        icon="close" />
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import {
  ADVANCED_OPERATOR_TYPE
} from '@/features/reports/common/advanced-options/config'
import { mapActions, mapState } from 'vuex'
import BaseSmartInputAutocomplete from '../../../../common/BaseSmartInputAutocomplete'
import BaseSmartInput from '../../../../common/BaseSmartInput'

export default {
  name: 'AdvancedFiltersRow',
  created() {
    this.$nextTick(() => {
      this.option = _.cloneDeep(this.item)
    })
  },
  props: {
    /**
     * list of appropriate for this page options set from {@link ./config}
     */
    options: {
      type: Array
    },
    /**
     * object that described start structure of this.option object
     * @example:
     *  id: 1575537813533
     *  isFirst: true
     *  selectedField: ""
     *  selectedFilter: Object
     *  selectedOperator: ""
     */
    item: {
      type: Object,
      required: true
    },
    /**
     * if true - start validation logic
     */
    isValidating: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      /**
       * @alias resultOption
   * object that will be changed as filling row columns
   * @example:
   *  id: 1575537813533
   *  isFirst: true
   *  selectedField: ""
   *  selectedFilter: {
       *  text: '',   // for {@link AdvancedChipsBlock} to display label of selected value
       *  value: ''   // for api calls - there could be or list item _id or string
       *  }
   *  selectedOperator: ""
   */
      option: {},
      ADVANCED_OPERATOR_TYPE,
      /*STRING_OPERATORS: stringOperators,*/
      // input used in BaseSmartInputAutocomplete
      inputText: null,
      smartInputFilterState: 'close',
      autocompleteTimeout: null,
      autocompleteFilters: []
    }
  },
  watch: {
    /**
     * watcher for autocomplete property in rankZoneCommon store
     */
    autocomplete: {
      handler: 'autocompleteHandler',
      deep: true
    },
    /**
     * callback for autocomplete mode
     * */
    'option.selectedField': {
      handler() {
        this.isAutocompleteMode && this.resetAutocompleteId({ id: this.option.id })
      },
      deep: true
    },
    /**
     * callback for autocomplete mode
     * */
    'option.selectedOperator': {
      handler(newVal, prevVal) {
        this.isAutocompleteMode && this.resetAutocompleteId({ id: this.option.id })
        if (this.option.selectedField === 'url') {
          if (newVal === 'contains') {
            Object.assign(this.option.selectedFilter, { value: this.option.selectedFilter.text })
          } else if (prevVal === 'contains') {
            const newFilter = this.filters.find(item => item.label === this.option.selectedFilter.text)
            if (!newFilter) {
              this.$refs.smartInput.clear()
            } else {
              Object.assign(this.option.selectedFilter, newFilter)
              this.onOptionChanged()
            }
          }
        }
      },
      deep: true
    },
    /**
     * callback invoked when selectedFilter was changed from input, {@link BaseSmartInputAutocomplete} or {@link BaseSmartInput}
     * */
    'option.selectedFilter': {
      handler(newFilter, oldFilter) {
        // for plane input field we need to fill in second params 'value' into selectedFilter property object
        if (this.isInputStringMode || this.isNumberInputMode || this.option.selectedOperator === 'contains') {
          Object.assign(this.option.selectedFilter, { value: newFilter.text })
        }
        this.onOptionChanged()
      },
      deep: true
    }
  },
  methods: {
    ...mapActions('rankZoneCommon', ['getAutocomplete', 'resetAutocompleteId']),
    /**
     * callback invoked if autocomplete property in rankZoneCommon store was changed
     * */
    autocompleteHandler(autocomplete) {
      const value = autocomplete[this.option.id]
      const filters = _.get(value, `list`, [])
      if (filters.length > 0 && !this.inputText) {
        this.resetAutocompleteId({ id: this.option.id })
        this.autocompleteFilters = []
      }
      this.autocompleteFilters = filters.map(item => ({ value: item, label: item }))
    },
    /**
     * callback for first column ('Field')
     * */
    onFieldChanged(value) {
      this.option.selectedFilter = {}
      this.option.selectedOperator = ''
      // this.option is filling by corresponding option from config options
      const newOption = this.clonedOptions.find(item => item.value === value)
      this.option = { ...this.option, ...newOption }
      this.onOptionChanged()
    },
    /**
     * invoked by each change of {@link #resultOption} object
     * could be invoked both from template and from code
     * */
    onOptionChanged() {
      this.$emit('update-item', this.option)
    },
    /**
     * callback for {@link BaseSmartInputAutocomplete}
     * using to make api call to load dropdown data
     * @param text - entered by user text
     */
    onAutocompleteInputTextChanged(text) {
      this.inputText = text || ''
      if (!text) {
        this.resetAutocompleteId({ id: this.option.id })
        return
      }
      // we use timeout here to avoid too frequent api calls
      // it's like _.debounce but more reliable
      if (this.autocompleteTimeout) {
        clearTimeout(this.autocompleteTimeout)
      }
      this.autocompleteTimeout = setTimeout(() => {
        this.getAutocomplete({
          clientId: this.clientId,
          id: this.option.id,
          filters: { start: this.inputText, type: this.option.type }
        })
      }, 750)
    },
    /**
     * callback for delete button
     */
    onDeleteOption() {
      this.$emit('delete')
    },
    /**
     * invoked when autocomplete dropdown statue was changed
     * @param value - 'close' or 'open'
     */
    onStateChange(value) {
      this.smartInputFilterState = value
    }
  },
  computed: {
    ...mapState('rankZoneCommon', ['autocomplete', 'isAutocompleteDataUpdating']),
    filters() {
      return _.get(this.option, 'operators.options', [])
    },
    filterType() {
      return _.get(this.option, 'operators.type', '')
    },
    isOperatorError() {
      return this.isValidating && _.isEmpty(this.option.selectedOperator)
    },
    isFieldError() {
      return this.isValidating && (_.isEmpty(this.option.selectedField) && parseInt(this.option.selectedField) !== 0)
    },
    isFilterError() {
      const validText = _.get(this.option, 'selectedFilter.text', '')
      return this.isValidating && (!validText && parseInt(validText) !== 0)
    },
    clonedOptions() {
      const sortedOptions = _.cloneDeep(this.options)
      // sort by asc
      return sortedOptions.sort((a, b) => (a.label > b.label) ? 1 : ((b.label > a.label) ? -1 : 0))
    },
    operators() {
      return _.get(this.option, 'operators.operatorVariants', [])
    },
    isAutocompleteMode() {
      return this.filterType === this.ADVANCED_OPERATOR_TYPE.AUTOCOMPLETE && this.option.selectedOperator === 'equal'
    },
    isSmartInputMode() {
      return this.filterType === ADVANCED_OPERATOR_TYPE.LIST
    },
    isInputStringMode() {
      return this.filterType === this.ADVANCED_OPERATOR_TYPE.STRING || (this.filterType === this.ADVANCED_OPERATOR_TYPE.AUTOCOMPLETE && this.option.selectedOperator !== 'equal')
    },
    isNumberInputMode() {
      return this.filterType === this.ADVANCED_OPERATOR_TYPE.NUMBER
    },
    disableWithoutOptions() {
      return _.get(this.option, 'disableWithoutOptions', false)
    },
    isSmartInputFocused() {
      return this.smartInputFilterState === 'open'
    }
  }
}
</script>

<style scoped
  lang="scss">
  .advanced-row {
    display: flex;
    align-items: center;
    gap: 8px;

    &__loader {
      position: absolute !important;
      top: -2px;
      left: 50%;
      transform: translateX(-50%);
    }

    .advanced-row__input-placeholder {
      border: 1px solid $rfx-light-march;
      border-radius: $rfx-default-border-radius;
      height: 28px;
      box-sizing: border-box;
      background-color: transparent;
      border-color: $rfx-silver;
      padding: 8px;
      font-size: 12px;
      display: block;
      width: 100%;

      &::placeholder {
        color: $rfx-silver;
      }
    }

    &__field,
    &__type,
    &__filter {
      flex: 1 1;

      ::v-deep .smart-select {
        height: 28px !important;

        &__header {
          position: relative;

          &::after {
            content: '';
            display: block;
            position: absolute;
            transform: rotate(45deg);
            width: 8px;
            height: 8px;
            border: 2px solid $rfx-dark-march;
            border-top-width: 0;
            border-left-width: 0;
            top: 8px;
            right: 10px;
          }
        }

        &__header {
          &_disabled {
            opacity: .75;

            &::after {
              border-color: $rfx-silver;
            }
          }
        }

        &__input {
          border: 1px solid $rfx-light-march;
          border-radius: $rfx-default-border-radius;
          font-size: 12px;
          font-weight: 400;
          padding: 8px;

          &::placeholder {
            font-size: 12px;
          }
        }
      }
    }

    &__select {
      flex: 1 1 90%;
      color: $rfx-dark-march;
      font-weight: 500;
    }

    &_error {
      border-bottom: 2px solid $rfx-light-red;
    }

    &__one-operator {
      display: flex;
      align-items: center;
      height: 100%;
      flex: 1 1 100%;
      font-size: 14px;
      padding-left: 20px;
      box-sizing: border-box;
      color: #000;
      @include ellipsis;
    }

    &__input {
      color: $rfx-dark-march;
      border: 1px solid $rfx-light-march;
      border-radius: $rfx-default-border-radius;
      font-size: 12px;
      font-weight: 400;
      height: 28px;
      box-sizing: border-box;
      padding: 8px;

      &::placeholder {
        color: $rfx-silver;
      }

      &_error {
        &::placeholder {
          color: $rfx-light-red;
          font-size: 13px;
        }
      }
    }

    &__button {
      flex: 0 0 16px;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      cursor: pointer;

      ::v-deep .material-design-icon {
        font-size: 16px;
        color: $rfx-silver;
      }
    }
  }
</style>
